import React, { useState, useRef } from "react";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { makeRequest2 } from "../../../../utils/makeRequest";
import { toastify } from "../../../../utils/toast";
import styles from "./powerInvoice.module.css";
import { getItem } from "../../../../utils/localStorage";
import {
    sendPowerInvoiceNotificationURL,
    voidInvoice
} from "../../../../utils/urls";

const PowerInvoiceActionsDropdown = ({
    invoice,
    facilityId,
    handleDownloadPDF,
    setShowReceiptsDialog,
    loading,
}) => {
    const [selectedAction, setSelectedAction] = useState(null);
    const [resending, setResending] = useState(false);
    const [voidingInvoice, setVoidingInvoice] = useState(false);
    const [showAlternativeNumbersDialog, setShowAlternativeNumbersDialog] = useState(false);
    const [alternativeNumbers, setAlternativeNumbers] = useState(['']);
    const [sendingToAlternative, setSendingToAlternative] = useState(false);

    const getAvailableActions = () => {
        const base = [
            { label: "Receipts",         value: "receipts", icon: "fas fa-receipt" },
            { label: "Download Invoice", value: "download", icon: "fas fa-download" },
        ];

        if (invoice) {
            base.push({ label: "Send to Client",             value: "resend",           icon: "fas fa-paper-plane" });
            base.push({ label: "Send to Alternative Number", value: "send-alternative", icon: "fas fa-mobile-alt"  });

            const s = (invoice.status || "").toLowerCase();
            if (s !== "voided" && s !== "void") {
                base.push({ label: "Void Invoice", value: "void-invoice", icon: "fas fa-ban" });
            }
        }

        return base;
    };

    const actions = getAvailableActions();

    // ── Action dispatcher ──────────────────────────────────────────────────────
    const handleActionChange = (e) => {
        const action = e.value;
        setSelectedAction(action);

        switch (action) {
            case "receipts":
                setShowReceiptsDialog(true);
                break;
            case "download":
                handleDownloadPDF();
                break;
            case "resend":
                handleResendInvoice();
                break;
            case "send-alternative":
                setAlternativeNumbers(['']);
                setShowAlternativeNumbersDialog(true);
                break;
            case "void-invoice":
                handleVoidInvoice();
                break;
            default:
                break;
        }

        setTimeout(() => setSelectedAction(null), 500);
    };

    // ── Resend to client ───────────────────────────────────────────────────────
    const handleResendInvoice = async () => {
    if (!invoice || !invoice._id) {
        toastify("Invalid invoice data", "error");
        return;
    }

    try {
        setResending(true);
        toastify("Sending power invoice SMS...", "info");

        const response = await makeRequest2(
            `${sendPowerInvoiceNotificationURL}/${facilityId}/send-notification/${invoice._id}`,
            "POST",
            { sendSms: true, sendEmail: false }
        );

        if (response.success) {
            toastify("Power invoice SMS sent successfully!", "success");
        } else {
            toastify(response.message || "Failed to send SMS notification", "error");
        }
    } catch (error) {
        console.error("Error sending power invoice SMS:", error);
        toastify("Failed to send SMS notification. Please try again.", "error");
    } finally {
        setResending(false);
    }
};

    // ── Void invoice ───────────────────────────────────────────────────────────
    const handleVoidInvoice = async () => {
        if (!invoice?._id) {
            toastify("Invalid invoice data", "error");
            return;
        }

        // Same user lookup as the water invoice void
        let currentUser = { userId: 'system', fullName: 'System Admin', role: 'Property Manager' };
        try {
            const userId = await getItem('selectedCustomerId') || await getItem('userId') || await getItem('currentUserId');
            const userInfo = await getItem('currentUser') || await getItem('user') || await getItem('userInfo');
            const parsed = typeof userInfo === 'string' ? JSON.parse(userInfo) : userInfo;
            currentUser = parsed?.userId ? parsed : { ...currentUser, userId: userId || 'system' };
        } catch (error) {
            console.warn("Could not read user info from storage", error);
        }

        confirmDialog({
            message:         "Are you sure you want to void this power invoice? This action cannot be undone.",
            header:          "Void Power Invoice",
            icon:            "pi pi-exclamation-triangle",
            acceptClassName: "p-button-danger",
            acceptLabel:     "Yes, Void Invoice",
            rejectLabel:     "Cancel",
            accept: async () => {
                try {
                    setVoidingInvoice(true);
                    toastify("Voiding power invoice...", "info");

                    const response = await makeRequest2(
                        `${voidInvoice}/${facilityId}/${invoice._id}`,
                        "POST",
                        {
                            reason: "Voided by property manager",
                            userId: currentUser.userId,
                            userName: currentUser.fullName || currentUser.firstName || currentUser.username || "System Admin",
                            userRole: currentUser.role || "Property Manager",
                        }
                    );

                    if (response.success || response.data) {
                        toastify("Power invoice successfully voided", "success");
                    } else {
                        toastify(response.message || "Failed to void power invoice", "error");
                    }
                } catch (error) {
                    console.error("Error voiding power invoice:", error);
                    toastify("Failed to void power invoice. Please try again.", "error");
                } finally {
                    setVoidingInvoice(false);
                }
            },
        });
    };

    // ── Alternative numbers helpers ────────────────────────────────────────────
    const validatePhone = (num) => {
        const clean = num.replace(/\D/g, '');
        return clean.length >= 10 && clean.length <= 15;
    };

    const updateNumber = (index, value) => {
        const updated = [...alternativeNumbers];
        updated[index] = value;
        setAlternativeNumbers(updated);
    };

    const removeNumber = (index) => {
        if (alternativeNumbers.length > 1)
            setAlternativeNumbers(alternativeNumbers.filter((_, i) => i !== index));
    };

    const handleSendToAlternativeNumbers = async () => {
        const valid = alternativeNumbers.filter(n => n.trim() && validatePhone(n.trim()));
        const invalid = alternativeNumbers.filter(n => n.trim() && !validatePhone(n.trim()));

        if (valid.length === 0) {
            toastify("Please enter at least one valid phone number", "warning");
            return;
        }
        if (invalid.length > 0) {
            toastify(`Invalid numbers detected: ${invalid.join(', ')}`, "error");
            return;
        }

        try {
            setSendingToAlternative(true);
            toastify(`Sending power invoice to ${valid.length} number(s)...`, "info");

            // TODO: replace URL with real alternative-send endpoint once available
            const response = await makeRequest2(
                `/api/core/power-invoices/resend-alternative/${facilityId}`,
                "POST",
                {
                    invoiceId:     invoice._id,
                    phoneNumbers:  valid,
                    invoiceNumber: invoice.invoiceNumber,
                    customerName:  invoice.CustomerInfo?.fullName || "Customer",
                    unitName:      invoice.unitName || "Unit",
                    amount:        invoice.powerCharges?.totalCharge || 0,
                    dueDate:       invoice.dueDate,
                    invoiceType:   "power",
                }
            );

            if (response.success) {
                toastify(`Power invoice sent to ${valid.length} number(s)!`, "success");
                setShowAlternativeNumbersDialog(false);
                setAlternativeNumbers(['']);
            } else {
                toastify(response.message || "Failed to send power invoice", "error");
            }
        } catch (error) {
            console.error("Error sending power invoice to alternative numbers:", error);
            toastify("Failed to send power invoice. Please try again.", "error");
        } finally {
            setSendingToAlternative(false);
        }
    };

    // ── Templates ──────────────────────────────────────────────────────────────
    const actionItemTemplate = (option) => (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <i className={option.icon}></i>
            <span>{option.label}</span>
        </div>
    );

    const selectedTemplate = (option, props) =>
        option ? actionItemTemplate(option) : <span>{props.placeholder}</span>;

    const isBusy = loading || resending || voidingInvoice || sendingToAlternative;

    // ──────────────────────────────────────────────────────────────────────────
    return (
        <>
            {/* ── Main dropdown ──────────────────────────────────────────── */}
            <div className={styles.dropdownContainer}>
                <Dropdown
                    value={selectedAction}
                    options={actions}
                    onChange={handleActionChange}
                    placeholder="Invoice Actions"
                    itemTemplate={actionItemTemplate}
                    valueTemplate={selectedTemplate}
                    className="w-full md:w-14rem"
                    disabled={isBusy}
                />
                {isBusy && (
                    <div className={styles.loaderIndicator}>
                        <i className="pi pi-spin pi-spinner" style={{ marginLeft: 10 }}></i>
                    </div>
                )}
            </div>

            {/* ── Void confirm ───────────────────────────────────────────── */}
            <ConfirmDialog />

            {/* ── Alternative numbers dialog ─────────────────────────────── */}
            <Dialog
                header="Send Power Invoice to Alternative Numbers"
                visible={showAlternativeNumbersDialog}
                style={{ width: 500 }}
                onHide={() => setShowAlternativeNumbersDialog(false)}
                draggable={false}
                modal
                footer={
                    <div className="d-flex justify-content-end gap-2">
                        <Button
                            label="Cancel"
                            icon="pi pi-times"
                            className="p-button-text p-button-secondary"
                            onClick={() => setShowAlternativeNumbersDialog(false)}
                            disabled={sendingToAlternative}
                        />
                        <Button
                            label={sendingToAlternative ? "Sending…" : "Send Invoice"}
                            icon="pi pi-send"
                            onClick={handleSendToAlternativeNumbers}
                            disabled={sendingToAlternative}
                        />
                    </div>
                }
            >
                <div className="p-fluid">
                    {/* Invoice summary */}
                    <div className="mb-3 p-3" style={{ background: "#f8f9fa", borderRadius: 6, border: "1px solid #dee2e6" }}>
                        <h6 className="mb-2">Invoice Summary</h6>
                        <div className="row g-1">
                            <div className="col-6"><small><strong>Invoice #:</strong> {invoice?.invoiceNumber}</small></div>
                            <div className="col-6"><small><strong>Amount:</strong> KSh {(invoice?.powerCharges?.totalCharge || 0).toLocaleString("en-KE", { minimumFractionDigits: 2 })}</small></div>
                            <div className="col-6"><small><strong>Unit:</strong> {invoice?.unitName || "N/A"}</small></div>
                            <div className="col-6"><small><strong>Due:</strong> {invoice?.dueDate ? new Date(invoice.dueDate).toLocaleDateString() : "N/A"}</small></div>
                        </div>
                    </div>

                    {/* Phone number inputs */}
                    <label className="fw-bold mb-2 d-block">Phone Numbers</label>
                    {alternativeNumbers.map((num, i) => (
                        <div key={i} className="d-flex align-items-center mb-2 gap-2">
                            <div className="flex-grow-1">
                                <InputText
                                    placeholder="e.g. 0712345678"
                                    value={num}
                                    onChange={(e) => updateNumber(i, e.target.value)}
                                    className={`w-full ${num.trim() && !validatePhone(num) ? "p-invalid" : ""}`}
                                    disabled={sendingToAlternative}
                                />
                                {num.trim() && !validatePhone(num) && (
                                    <small className="p-error">Invalid phone number</small>
                                )}
                            </div>
                            {alternativeNumbers.length > 1 && (
                                <Button
                                    icon="pi pi-trash"
                                    className="p-button-danger p-button-text p-button-sm"
                                    onClick={() => removeNumber(i)}
                                    disabled={sendingToAlternative}
                                />
                            )}
                        </div>
                    ))}

                    <Button
                        label="Add Another Number"
                        icon="pi pi-plus"
                        className="p-button-outlined p-button-sm mt-1"
                        onClick={() => setAlternativeNumbers([...alternativeNumbers, ''])}
                        disabled={sendingToAlternative || alternativeNumbers.length >= 10}
                    />

                    <small className="text-muted d-block mt-3">
                        <i className="pi pi-info-circle me-1"></i>
                        SMS will be sent to all valid numbers with the power invoice details.
                    </small>
                </div>
            </Dialog>
        </>
    );
};

export default PowerInvoiceActionsDropdown;